import React, { useState } from 'react';
import PropTypes from 'prop-types';
import {
  ModalRoot,
  ModalBackdrop,
  ModalPositioner,
  ModalContent,
  ModalTitle,
} from '@rcw/ui';
import { useTranslation } from 'i18n/LocaleContext';
import { useCancelSubscriptionMutation, useSendCancellationSurveyMutation } from 'app/api/gatewayApiSlice';

const REASON_KEYS = ['QUALITY', 'INSTRUMENTS', 'FINANCIAL', 'DISUSE'];

// Fisher-Yates shuffle. Reasons are shown in random order to avoid users picking the first option by default.
function shuffle(items) {
  const result = [...items];
  for (let i = result.length - 1; i > 0; i -= 1) {
    const j = Math.floor(Math.random() * (i + 1));
    [result[i], result[j]] = [result[j], result[i]];
  }
  return result;
}

function CloseIcon() {
  return (
    <svg xmlns='http://www.w3.org/2000/svg' width='24' height='24' fill='none'>
      <path stroke='#39a9f7' strokeLinecap='round' strokeLinejoin='round' strokeWidth='2' d='M12 22c5.523 0 10-4.477 10-10S17.523 2 12 2 2 6.477 2 12s4.477 10 10 10M15 9l-6 6M9 9l6 6' />
    </svg>
  );
}

export function SubscriptionCancellationDialog({
  sub,
  isOpen,
  onOpenChange,
  idToken,
  onCancelled,
}) {
  const { t } = useTranslation();
  const [step, setStep] = useState(1);
  const [reason, setReason] = useState('');
  const [comment, setComment] = useState('');

  const [cancelSub, { isLoading: isCancelling }] = useCancelSubscriptionMutation();
  const [sendSurvey, { isLoading: isSendingSurvey }] = useSendCancellationSurveyMutation();

  // Reshuffle each time the dialog opens, but keep the order stable while it stays open.
  const [reasonKeys, setReasonKeys] = useState(() => shuffle(REASON_KEYS));
  const [wasOpen, setWasOpen] = useState(isOpen);
  if (isOpen !== wasOpen) {
    setWasOpen(isOpen);
    if (isOpen) setReasonKeys(shuffle(REASON_KEYS));
  }

  const reasons = reasonKeys.map((key) => ({
    key,
    label: t(`RVS.COMPONENT.CANCEL_SUBSCRIPTION.CANCEL_REASON_${key}`),
  }));

  const handleClose = () => {
    onOpenChange({ open: false });
    setTimeout(() => {
      setStep(1);
      setReason('');
      setComment('');
    }, 200);
  };

  const handleConfirm = async () => {
    try {
      await sendSurvey({
        subscriptionId: sub.subscriptionId,
        cancellationReason: reason,
        cancellationComment: comment,
        idToken,
      }).unwrap();

      await cancelSub({
        subscriptionId: sub.subscriptionId,
        idToken,
      }).unwrap();

      if (onCancelled) onCancelled();
      handleClose();
    } catch (err) {
      console.error('[SubscriptionCancellationDialog] Error:', err);
    }
  };

  return (
    <ModalRoot
      open={isOpen}
      onOpenChange={onOpenChange}
    >
      <ModalBackdrop className='!bg-black/80' />
      <ModalPositioner className='!p-0'>
        <ModalContent className='!max-w-4xl !rounded-3xl !shadow-2xl md:p-8 relative mx-2 bg-white text-black text-sm'>
          <div className='relative'>
            <button
              type='button'
              onClick={handleClose}
              className='absolute top-5 right-4 md:top-0 md:right-0 p-1 md:p-0 hover:bg-black/5 rounded-full transition-colors'
              aria-label='Close Dialog'
            >
              <CloseIcon />
            </button>
            <div className='px-6 pt-6'>
              <ModalTitle className='!text-2xl !text-black'>
                {t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.CANCEL_CONFIRM_TITLE')}
              </ModalTitle>
            </div>
          </div>

          <div className='px-6 py-6'>
            {step === 1 ? (
              <div className='space-y-6'>
                <p>{t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.HTML.DESCRIPTION_01')}</p>
                <p className='font-bold'>{t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.HTML.DESCRIPTION_02')}</p>

                <div className='space-y-4'>
                  {reasons.map((r) => (
                    <label key={r.key} className='flex items-center gap-3 cursor-pointer group'>
                      <input
                        type='radio'
                        name='cancellationReason'
                        value={r.label}
                        checked={reason === r.label}
                        onChange={(e) => setReason(e.target.value)}
                        className='w-5 h-5 accent-roland-blue cursor-pointer'
                      />
                      <span className='group-hover:text-roland-blue transition-colors'>{r.label}</span>
                    </label>
                  ))}
                </div>

                <div className='space-y-2'>
                  <p className='text-sm font-medium'>{t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.HTML.DESCRIPTION_03')}</p>
                  <textarea
                    value={comment}
                    onChange={(e) => setComment(e.target.value)}
                    className='w-full min-h-[100px] p-4 rounded-xl border border-black/20 focus:border-roland-blue focus:ring-1 focus:ring-roland-blue outline-none transition-all'
                  />
                </div>

                <div className='flex flex-col md:flex-row justify-end gap-4 pt-4'>
                  <button
                    type='button'
                    onClick={handleClose}
                    className='btn-secondary order-2 md:order-1'
                  >
                    {t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CANCEL', 'Cancel')}
                  </button>
                  <button
                    type='button'
                    disabled={!reason}
                    onClick={() => setStep(2)}
                    className={`btn-destructive order-1 md:order-2 ${!reason ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    {t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.BUTTON.SUBMIT', 'Cancel Subscription')}
                  </button>
                </div>
              </div>
            ) : (
              <div className='space-y-6'>
                <div className='bg-gray-50 p-6 rounded-2xl border border-black/5'>
                  <p className='text-sm leading-relaxed'>
                    {t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.CANCEL_REASON_CONFIRM_PREFACE')}
                    {' '}
                    <strong>{sub.offer?.description || sub.tierDescription}</strong>
                    {' '}
                    {t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.CANCEL_REASON_CONFIRM_CLOSING')}
                  </p>
                  <p className='mt-3 font-bold text-roland-blue italic'>
                    &quot;
                    {reason}
                    &quot;
                  </p>
                </div>

                <p className='text-sm text-gray-600 leading-relaxed'>
                  {t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.HTML.CANCEL_CONFIRM_DESCRIPTION')}
                </p>

                <div className='flex flex-col md:flex-row justify-end gap-4 pt-4'>
                  <button
                    type='button'
                    disabled={isCancelling || isSendingSurvey}
                    onClick={() => setStep(1)}
                    className='btn-secondary order-2 md:order-1'
                  >
                    {t('RVS.COMPONENT.LOYALTY_PROGRAM.BUTTON.CANCEL', 'Back')}
                  </button>
                  <button
                    type='button'
                    disabled={isCancelling || isSendingSurvey}
                    onClick={handleConfirm}
                    className='btn-destructive order-1 md:order-2 flex items-center justify-center min-w-[140px]'
                  >
                    {isCancelling || isSendingSurvey ? (
                      <span className='w-5 h-5 border-2 border-white/30 border-t-white rounded-full animate-spin' />
                    ) : (
                      t('RVS.COMPONENT.CANCEL_SUBSCRIPTION.BUTTON.CONFIRM', 'Confirm Subscription Cancelation')
                    )}
                  </button>
                </div>
              </div>
            )}
          </div>
        </ModalContent>
      </ModalPositioner>
    </ModalRoot>
  );
}

SubscriptionCancellationDialog.propTypes = {
  sub: PropTypes.shape({
    subscriptionId: PropTypes.number,
    tierDescription: PropTypes.string,
    offer: PropTypes.shape({ description: PropTypes.string }),
  }).isRequired,
  isOpen: PropTypes.bool.isRequired,
  onOpenChange: PropTypes.func.isRequired,
  idToken: PropTypes.string.isRequired,
  onCancelled: PropTypes.func,
};
